import { mapState } from 'vuex';
import get from 'lodash/get';
import ApiService from '@/services/apiService';
import { NO_DEVICE_DRIVER, OPERATIONAL_STATUS_LABEL_KEYS, fireflyConnectors, prefillDeviceTypeMapping, isMatchedById, mappedDriverCount, buildMigrationPayload, describeStatusDistribution } from '@/utils/iotHubMigration';

// the device driver list caps a page at 100 rows (dd.fetch MAX_PAGE_SIZE)
const DRIVER_PAGE_SIZE = 100;
const defaultImportOptions = () => ({
  address: true,
  tags: true,
  description: true
});
export default {
  name: 'NiotaClassicApplicationsMigrateDialog',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    // the IoT Data Hub application as the details view loads it ({id, title, account: {id, name}})
    application: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      preview: null,
      previewError: null,
      isLoadingPreview: false,
      isLoadingTenant: false,
      isStarting: false,
      accounts: [],
      selectedAccount: null,
      bridges: [],
      selectedBridgeId: null,
      parentId: null,
      groupItems: [],
      groups: [],
      drivers: [],
      // device type id -> driver id (NO_DEVICE_DRIVER for none); `userMapping` holds only the rows the user
      // changed, so a later load of the account's drivers can still prefill the untouched rows by id
      mapping: {},
      userMapping: {},
      importOptions: defaultImportOptions(),
      statusMode: 'keep',
      statusValue: '3'
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled']),
    applicationAccountId() {
      return get(this.application, 'account.id', null);
    },
    summary() {
      return get(this.preview, 'summary', {}) || {};
    },
    total() {
      return this.summary.total || 0;
    },
    migratableCount() {
      return this.summary.migratable || 0;
    },
    // migratable devices that already exist as virtual devices in the selected account (skipped by the job)
    alreadyMigratedCount() {
      return get(this.preview, 'alreadyMigrated') || 0;
    },
    // what a job would create now: the migratable devices minus the ones that already exist
    toCreateCount() {
      return Math.max(0, this.migratableCount - this.alreadyMigratedCount);
    },
    subtitle() {
      return this.$i18n_tc('NiotaClassic.Applications.migration.subtitle', this.total, {
        title: this.application.title,
        id: this.application.id,
        n: this.total
      });
    },
    fireflyBridges() {
      return fireflyConnectors(this.bridges);
    },
    fireflyBridgeItems() {
      return this.fireflyBridges.map(bridge => ({
        text: `Firefly · ${bridge.name} (ID ${bridge.id})`,
        value: bridge.id
      }));
    },
    showNoConnectorHint() {
      return !this.isLoadingTenant && Boolean(this.selectedAccount) && this.fireflyBridges.length === 0;
    },
    selectedAccountName() {
      const account = this.accounts.find(item => item.id === this.selectedAccount);
      return account ? account.name : '';
    },
    driverItems() {
      return [{
        text: this.$i18n_t('NiotaClassic.Applications.migration.mappingNoDriver'),
        value: NO_DEVICE_DRIVER
      }, ...this.drivers.map(driver => ({
        text: `${driver.title} (ID ${driver.id})`,
        value: driver.id
      }))];
    },
    deviceTypeRows() {
      return (this.summary.deviceTypes || []).map(deviceType => {
        const id = deviceType.id === undefined ? null : deviceType.id;
        const key = id === null ? 'none' : String(id);
        return {
          key,
          id,
          title: deviceType.title || this.$i18n_t('NiotaClassic.Applications.migration.mappingNoDeviceType'),
          deviceCount: deviceType.deviceCount,
          matchedById: isMatchedById(id, this.mapping[key])
        };
      });
    },
    mappableTypeCount() {
      return this.deviceTypeRows.filter(row => row.id !== null).length;
    },
    mappingStatusText() {
      const mapped = mappedDriverCount(this.mapping);
      if (this.mappableTypeCount > 0 && mapped === this.mappableTypeCount) {
        return this.$i18n_t('NiotaClassic.Applications.migration.allMapped');
      }
      return this.$i18n_t('NiotaClassic.Applications.migration.mappedCount', {
        mapped,
        total: this.mappableTypeCount
      });
    },
    importOptionRows() {
      const params = n => ({
        n: n || 0,
        total: this.total
      });
      return [{
        key: 'address',
        label: this.$i18n_t('NiotaClassic.Applications.migration.importAddress'),
        hint: this.$i18n_t('NiotaClassic.Applications.migration.importAddressHint', params(this.summary.withAddress))
      }, {
        key: 'tags',
        label: this.$i18n_t('NiotaClassic.Applications.migration.importTags'),
        hint: this.$i18n_t('NiotaClassic.Applications.migration.importTagsHint', params(this.summary.withTags))
      }, {
        key: 'description',
        label: this.$i18n_t('NiotaClassic.Applications.migration.importDescription'),
        hint: this.$i18n_t('NiotaClassic.Applications.migration.importDescriptionHint', params(this.summary.withDescription))
      }];
    },
    statusItems() {
      return Object.entries(OPERATIONAL_STATUS_LABEL_KEYS).map(([value, key]) => ({
        text: this.$i18n_t(key),
        value
      }));
    },
    keepStatusLabel() {
      const distribution = describeStatusDistribution(this.summary.operationalStatus, key => this.$i18n_t(key));
      return this.$i18n_t('NiotaClassic.Applications.migration.keepStatus', {
        distribution: distribution || '-'
      });
    },
    footerText() {
      if (!this.preview) {
        return '';
      }
      return this.$i18n_tc('NiotaClassic.Applications.migration.footer', this.toCreateCount, {
        n: this.toCreateCount,
        account: this.selectedAccountName
      });
    },
    // the notices under the options: what the job will skip, and whether anything is left to migrate
    notices() {
      const notices = [];
      const plural = (key, n) => this.$i18n_tc(`NiotaClassic.Applications.migration.${key}`, n, {
        n
      });
      if (this.summary.nonFirefly) {
        notices.push({
          key: 'nonFirefly',
          type: 'info',
          icon: 'fal fa-info-circle',
          text: plural('skippedNonFirefly', this.summary.nonFirefly)
        });
      }
      if (this.summary.withoutEui) {
        notices.push({
          key: 'noEui',
          type: 'info',
          icon: 'fal fa-info-circle',
          text: plural('skippedNoEui', this.summary.withoutEui)
        });
      }
      if (this.alreadyMigratedCount && this.toCreateCount === 0) {
        // every migratable device exists already: a job would only skip, so nothing can be started
        notices.push({
          key: 'allAlreadyMigrated',
          type: 'warning',
          icon: 'fal fa-exclamation-triangle',
          text: this.$i18n_t('NiotaClassic.Applications.migration.allAlreadyMigrated')
        });
      } else if (this.alreadyMigratedCount) {
        notices.push({
          key: 'alreadyMigrated',
          type: 'info',
          icon: 'fal fa-info-circle',
          text: plural('alreadyMigrated', this.alreadyMigratedCount)
        });
      }
      if (this.preview && this.migratableCount === 0) {
        notices.push({
          key: 'nothingToMigrate',
          type: 'warning',
          icon: 'fal fa-exclamation-triangle',
          text: this.$i18n_t('NiotaClassic.Applications.migration.nothingToMigrate')
        });
      }
      // informational notices use the application's accent color (the theme's "info" is teal); warnings
      // keep the warning color
      return notices.map(notice => ({
        ...notice,
        color: notice.type === 'info' ? this.color : notice.type
      }));
    },
    canStart() {
      return Boolean(this.preview && this.toCreateCount > 0 && this.selectedAccount && this.selectedBridgeId && this.parentId && !this.isStarting);
    },
    payload() {
      return buildMigrationPayload({
        application: this.application,
        tenantId: this.selectedAccount,
        bridgeId: this.selectedBridgeId,
        parentId: this.parentId,
        groups: this.groups,
        mapping: this.mapping,
        importOptions: this.importOptions,
        statusMode: this.statusMode,
        statusValue: this.statusValue
      });
    }
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.initialize();
      }
    },
    selectedAccount(tenantId) {
      if (!tenantId) {
        return;
      }
      this.loadTenantData();
      this.loadPreview();
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    // a typed group name that none of the account's virtual devices uses yet
    isNewGroup(group) {
      return !this.groupItems.includes(group);
    },
    removeGroup(group) {
      this.groups = this.groups.filter(existing => existing !== group);
    },
    async initialize() {
      this.preview = null;
      this.previewError = null;
      this.importOptions = defaultImportOptions();
      this.statusMode = 'keep';
      this.statusValue = '3';
      this.groups = [];
      this.mapping = {};
      this.userMapping = {};
      await this.loadAccounts();
      if (this.selectedAccount === this.applicationAccountId) {
        // reopened for the same account: the watcher does not fire, load explicitly
        this.loadTenantData();
        this.loadPreview();
        return;
      }
      this.selectedAccount = this.applicationAccountId;
    },
    async loadAccounts() {
      try {
        const accounts = await this.$store.dispatch('app/loadAccounts');
        // the root tenant has no twin to attach the new devices to
        this.accounts = (accounts || []).filter(tenant => !tenant.isRootTenant);
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('NiotaClassic.Applications.snacks.fetchAccountFailed'), 'error');
      }
    },
    async loadPreview() {
      this.isLoadingPreview = true;
      this.previewError = null;
      try {
        this.preview = await ApiService.fetchIotHubMigrationPreview(this.application.id, this.selectedAccount);
        this.mapping = prefillDeviceTypeMapping(this.summary.deviceTypes, this.drivers, this.userMapping);
      } catch (error) {
        console.error(error);
        this.previewError = this.errorMessage(error, 'previewFailed');
      } finally {
        this.isLoadingPreview = false;
      }
    },
    async loadTenantData() {
      const tenantId = this.selectedAccount;
      this.isLoadingTenant = true;
      this.selectedBridgeId = null;
      this.parentId = null;
      this.bridges = [];
      this.drivers = [];
      try {
        const [bridges, rootTwins, groupsResult, drivers] = await Promise.all([ApiService.getBridgesForTenant(tenantId), ApiService.digitalTwinFetchChildren(this, null, 0, 1, null, ['bare'], false, tenantId), ApiService.virtualDeviceFetchGroups(this, [tenantId]), this.fetchAllDrivers(tenantId)]);
        if (tenantId !== this.selectedAccount) {
          // the account changed while loading, a newer load owns the state
          return;
        }
        this.bridges = bridges || [];
        if (this.fireflyBridges.length === 1) {
          this.selectedBridgeId = this.fireflyBridges[0].id;
        }
        this.parentId = get(rootTwins, 'digitaltwins[0].id', null);
        this.groupItems = get(groupsResult, 'groupList', []) || [];
        this.drivers = drivers;
        this.mapping = prefillDeviceTypeMapping(this.summary.deviceTypes, this.drivers, this.userMapping);
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t('NiotaClassic.Applications.migration.targetLoadFailed'), 'error');
      } finally {
        if (tenantId === this.selectedAccount) {
          this.isLoadingTenant = false;
        }
      }
    },
    // every device driver the account can use: /devicedrivers/list answers {totalCount, deviceDrivers} with at
    // most 100 rows per page, so page until totalCount is reached (an empty page ends it as well)
    async fetchAllDrivers(tenantId, pageSize = DRIVER_PAGE_SIZE) {
      const drivers = [];
      let offset = 0;
      let totalCount = Infinity;
      while (drivers.length < totalCount) {
        const page = await ApiService.virtualDeviceDrivers.fetch({
          accountIds: [tenantId],
          fields: 'id,title',
          offset,
          limit: pageSize,
          sortBy: 'title',
          sortDesc: false
        });
        const rows = get(page, 'deviceDrivers') || [];
        if (!rows.length) {
          break;
        }
        drivers.push(...rows);
        totalCount = typeof page.totalCount === 'number' ? page.totalCount : drivers.length;
        offset += pageSize;
      }
      return drivers;
    },
    setMapping(key, value) {
      this.$set(this.mapping, key, value);
      this.$set(this.userMapping, key, value);
    },
    mappingCaption(row) {
      if (row.matchedById) {
        return this.$i18n_t('NiotaClassic.Applications.migration.mappingMatched');
      }
      if (!this.mapping[row.key] || this.mapping[row.key] === NO_DEVICE_DRIVER) {
        return this.$i18n_t('NiotaClassic.Applications.migration.mappingNoDriverHint');
      }
      return '';
    },
    parentSelectorFilter(item) {
      // only parents of the target account
      return item.tenantId === this.selectedAccount;
    },
    errorMessage(error, fallbackKey) {
      const code = get(error, 'response.data.message');
      if (code) {
        const translated = this.$i18n_t(`Error.${code}`);
        if (translated && translated !== `Error.${code}`) {
          return translated;
        }
      }
      return this.$i18n_t(`NiotaClassic.Applications.migration.${fallbackKey}`);
    },
    async start() {
      if (!this.canStart) {
        return;
      }
      this.isStarting = true;
      try {
        const response = await ApiService.createBackgroundRunnerIotHubMigration(this.payload);
        const runner = get(response, 'data', {});
        this.$snack(this.$i18n_t('NiotaClassic.Applications.migration.started', {
          id: runner.id || '-'
        }), 'success');
        this.$emit('started', runner);
        this.close();
      } catch (error) {
        console.error(error);
        this.$snack(this.errorMessage(error, 'startFailed'), 'error');
      } finally {
        this.isStarting = false;
      }
    }
  }
};